<script setup lang="ts">
import {
  ArrowLeft as ArrowLeftIcon,
  ChevronDown as ChevronDownIcon,
  Check as CheckIcon,
  Copy as CopyIcon,
  Download as DownloadIcon,
  Eye as EyeIcon,
  EyeOff as EyeOffIcon,
  SquarePlus as SquarePlusIcon,
  Maximize as MaximizeIcon,
  Pencil as PencilIcon,
  Pin as PinIcon,
  PinOff as PinOffIcon,
  Trash2 as Trash2Icon,
  X as XIcon,
} from '@lucide/vue'
import { computed, nextTick, ref, watch } from 'vue'

import BaseActionButton from '@/components/ui/BaseActionButton.vue'
import BaseIconButton from '@/components/ui/BaseIconButton.vue'
import { useEventListener } from '@/composables/useEventListener'
import {
  openAddToCollection,
  poiToAddToCollectionPoint,
  waypointToAddToCollectionPoint,
} from '@/services/addToCollectionService'
import { downloadFile } from '@/services/downloadService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { openMobileRenameSheet } from '@/services/mobileRenameService'
import { hasPermission, requirePermission } from '@/services/permissionService'
import { togglePOIPinned } from '@/services/pinnedService'
import { getPOIIcon } from '@/services/poiService'
import { showError, showSuccess } from '@/services/toastService'
import { isSmallScreen } from '@/services/uiService'
import { useAuthStore } from '@/stores/authStore'
import { useLibraryStore } from '@/stores/libraryStore'
import { useUiStore } from '@/stores/uiStore'
import { useWorkspaceStore } from '@/stores/workspaceStore'
import type { WorkspacePOI } from '@/types/poi'
import type { Waypoint } from '@/types/tour'
import { getCreatorByline } from '@/utils/creatorByline'
import { getEffectiveTourName } from '@/utils/tourName'

type DetailMode = 'workspace-poi' | 'tour-waypoint'

type POITypeOption = {
  value: string
  label: string
}

const props = withDefaults(
  defineProps<{
    mode?: DetailMode
    poi?: WorkspacePOI | null
    waypoint?: Waypoint | null
    tourId?: string
    tourName?: string
    waypointIndex?: number | null
    isLibraryContext?: boolean
    waypointVisible?: boolean
    showHeaderControls?: boolean
  }>(),
  {
    mode: 'workspace-poi',
    poi: null,
    waypoint: null,
    tourId: undefined,
    tourName: undefined,
    waypointIndex: null,
    isLibraryContext: false,
    waypointVisible: true,
    showHeaderControls: true,
  },
)

const emit = defineEmits<{
  close: []
  back: []
  'zoom-poi': [poiId: string]
  'zoom-waypoint': [tourId: string, waypointIndex: number]
  'delete-poi': [poiId: string]
  'delete-waypoint': [tourId: string, waypointIndex: number]
  'toggle-waypoint-visibility': []
  'retain-unpinned-poi-detail': [poi: WorkspacePOI]
  'replace-poi-selection': [poiId: string]
}>()

const poiTypeOptions: POITypeOption[] = [
  { value: '', label: 'None' },
  { value: 'generic', label: 'Generic' },
  { value: 'summit', label: 'Summit' },
  { value: 'valley', label: 'Valley' },
  { value: 'water', label: 'Water' },
  { value: 'food', label: 'Food' },
  { value: 'danger', label: 'Danger' },
  { value: 'accommodation', label: 'Accommodation' },
  { value: 'shelter', label: 'Shelter' },
  { value: 'parking', label: 'Parking' },
  { value: 'transport', label: 'Transport' },
  { value: 'bus', label: 'Bus' },
  { value: 'ferry', label: 'Ferry' },
  { value: 'landmark', label: 'Landmark' },
  { value: 'building', label: 'Building' },
  { value: 'car', label: 'Car' },
  { value: 'church', label: 'Church' },
  { value: 'fuel', label: 'Fuel' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'shopping', label: 'Shopping' },
  { value: 'information', label: 'Information' },
  { value: 'railway', label: 'Railway' },
  { value: 'forest', label: 'Forest' },
]

const workspaceStore = useWorkspaceStore()
const libraryStore = useLibraryStore()
const uiStore = useUiStore()
const authStore = useAuthStore()

const isWaypointMode = computed(() => props.mode === 'tour-waypoint')
const currentPoint = computed(() => (isWaypointMode.value ? props.waypoint : props.poi))
const pointCreatorByline = computed(() =>
  isWaypointMode.value || !props.poi ? null : getCreatorByline(props.poi),
)
const currentPointId = computed(() => currentPoint.value?.clientId ?? '')
const isPinnedWorkspace = computed(
  () => workspaceStore.isSystem === true && workspaceStore.systemKey === 'pinned',
)
const isPinnedPOI = computed(() => props.poi?.isPinned === true)
const waypointTourReferenceName = computed(() => {
  if (!isWaypointMode.value || !props.tourId) return props.tourName

  const tour = props.isLibraryContext
    ? libraryStore.detailTour?.clientId === props.tourId
      ? libraryStore.detailTour
      : null
    : workspaceStore.selectedTour?.clientId === props.tourId
      ? workspaceStore.selectedTour
      : null

  if (tour) return getEffectiveTourName(tour)
  return props.tourName
})
const pointNameDisplayClass = computed(() =>
  isWaypointMode.value
    ? 'truncate text-base font-semibold text-gray-900'
    : 'truncate text-xl font-semibold text-gray-900',
)
const pointNameInputClass = computed(() =>
  isWaypointMode.value
    ? 'w-full bg-transparent text-center text-base font-semibold text-gray-900 outline-none'
    : 'w-full bg-transparent text-center text-lg font-semibold text-gray-900 outline-none',
)

// Visibility only — creator-only edit/delete is enforced (with an explanatory dialog) by the
// store action itself (updateWaypoint/deleteWaypoint or updateWorkspacePOI/deleteWorkspacePOI),
// so the affordance stays visible to every collaborator.
const canEditPoint = computed(() => hasPermission('edit_waypoint'))
const canDeletePoint = computed(
  () => hasPermission('delete_waypoint') && (isWaypointMode.value || !isPinnedWorkspace.value),
)
const isPointVisible = computed(() =>
  isWaypointMode.value ? props.waypointVisible : !uiStore.isPOIHidden(currentPointId.value),
)

const isNameEditing = ref(false)
const nameDraft = ref('')
const nameInputRef = ref<HTMLInputElement | null>(null)

const isDescriptionEditing = ref(false)
const descriptionDraft = ref('')
const descriptionExpanded = ref(false)
const descriptionTextareaRef = ref<HTMLTextAreaElement | null>(null)
const selectedPointType = ref(currentPoint.value?.type ?? '')
const pointIcon = computed(() => getPOIIcon(selectedPointType.value || undefined))
const selectedPointTypeLabel = computed(() => {
  const match = poiTypeOptions.find((option) => option.value === selectedPointType.value)
  return match?.label ?? 'None'
})

const isTypeDropdownOpen = ref(false)
const typeButtonRef = ref<HTMLElement | null>(null)
const typeDropdownStyle = ref<Record<string, string>>({})

watch(
  () => [props.mode, currentPointId.value] as const,
  () => {
    nameDraft.value = currentPoint.value?.name ?? ''
    descriptionDraft.value = currentPoint.value?.description ?? ''
    descriptionExpanded.value = false
    isNameEditing.value = false
    isDescriptionEditing.value = false
    selectedPointType.value = currentPoint.value?.type ?? ''
    isTypeDropdownOpen.value = false
  },
  { immediate: true },
)

watch(
  () => currentPoint.value?.name,
  (value) => {
    if (!isNameEditing.value) {
      nameDraft.value = value ?? ''
    }
  },
)

watch(
  () => currentPoint.value?.description,
  (value) => {
    if (!isDescriptionEditing.value) {
      descriptionDraft.value = value ?? ''
    }
  },
)

watch(
  () => currentPoint.value?.type,
  (value) => {
    selectedPointType.value = value ?? ''
  },
)

const formatCoordinate = (value: number) => value.toFixed(5)

const coordinateText = computed(() => {
  const point = currentPoint.value
  if (!point) return ''
  return `${formatCoordinate(point.lat)}, ${formatCoordinate(point.lon)}`
})

const elevationText = computed(() => {
  const point = currentPoint.value
  return typeof point?.ele === 'number' ? `${Math.round(point.ele)} m` : null
})

const updateWaypoint = async (updates: Partial<Waypoint>) => {
  if (!props.tourId || props.waypointIndex === null) return

  if (props.isLibraryContext) {
    await libraryStore.updateWaypoint(props.tourId, props.waypointIndex, updates)
    return
  }

  await workspaceStore.updateWaypoint(props.tourId, props.waypointIndex, updates)
}

const updateWorkspacePOI = async (updates: Partial<WorkspacePOI>) => {
  const poiId = props.poi?.clientId
  if (!poiId) return
  await workspaceStore.updateWorkspacePOI(poiId, updates)
}

const updateCurrentPoint = async (updates: Partial<Waypoint> | Partial<WorkspacePOI>) => {
  if (isWaypointMode.value) {
    await updateWaypoint(updates as Partial<Waypoint>)
    return
  }

  await updateWorkspacePOI(updates as Partial<WorkspacePOI>)
}

const startNameEdit = async () => {
  if (!canEditPoint.value) return
  if (!(await requirePermission('edit_waypoint'))) return

  if (isSmallScreen.value) {
    const entityLabel = isWaypointMode.value ? 'waypoint' : 'POI'
    const currentName = currentPoint.value?.name ?? (isWaypointMode.value ? 'Waypoint' : 'POI')
    openMobileRenameSheet({
      title: `Rename ${entityLabel}`,
      currentName,
      submit: async (name) => {
        if (name === currentPoint.value?.name) return
        await updateCurrentPoint({ name })
      },
    })
    return
  }

  nameDraft.value = currentPoint.value?.name ?? ''
  isNameEditing.value = true
  await nextTick()
  nameInputRef.value?.focus()
  nameInputRef.value?.select()
}

const cancelNameEdit = () => {
  isNameEditing.value = false
  nameDraft.value = currentPoint.value?.name ?? ''
}

const saveNameEdit = async () => {
  if (!isNameEditing.value) return
  const nextName = nameDraft.value.trim()
  isNameEditing.value = false
  if (nextName === (currentPoint.value?.name ?? '')) return

  try {
    await updateCurrentPoint({ name: nextName || undefined })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update point name', { error })
    showError(
      `Failed to save ${isWaypointMode.value ? 'waypoint' : 'POI'} name: ${
        error instanceof Error ? error.message : String(error)
      }`,
    )
    nameDraft.value = currentPoint.value?.name ?? ''
  }
}

const autoSizeDescriptionTextarea = () => {
  const textarea = descriptionTextareaRef.value
  if (!textarea) return
  textarea.style.height = 'auto'
  textarea.style.height = `${textarea.scrollHeight}px`
}

const startDescriptionEdit = async () => {
  if (!canEditPoint.value) return
  if (!(await requirePermission('edit_waypoint'))) return
  descriptionDraft.value = currentPoint.value?.description ?? ''
  isDescriptionEditing.value = true
  await nextTick()
  autoSizeDescriptionTextarea()
  descriptionTextareaRef.value?.focus()
}

const cancelDescriptionEdit = () => {
  isDescriptionEditing.value = false
  descriptionDraft.value = currentPoint.value?.description ?? ''
}

const saveDescriptionEdit = async () => {
  const nextDescription = descriptionDraft.value.trim()
  isDescriptionEditing.value = false
  if (nextDescription === (currentPoint.value?.description ?? '')) return

  try {
    await updateCurrentPoint({ description: nextDescription || undefined })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update point description', { error })
    showError(
      `Failed to save ${isWaypointMode.value ? 'waypoint' : 'POI'} description: ${
        error instanceof Error ? error.message : String(error)
      }`,
    )
    descriptionDraft.value = currentPoint.value?.description ?? ''
  }
}

const setFloatingPanelPosition = (
  trigger: HTMLElement | null,
  panelWidth: number,
  stylesRef: typeof typeDropdownStyle,
) => {
  if (!trigger) return
  const rect = trigger.getBoundingClientRect()
  const viewportPadding = 8
  const left = Math.min(
    Math.max(rect.left, viewportPadding),
    window.innerWidth - panelWidth - viewportPadding,
  )
  const spaceBelow = window.innerHeight - rect.bottom
  const nextStyle: Record<string, string> = {
    position: 'fixed',
    left: `${left}px`,
    width: `${panelWidth}px`,
  }

  if (spaceBelow >= 280) {
    nextStyle.top = `${rect.bottom + 8}px`
  } else {
    nextStyle.bottom = `${window.innerHeight - rect.top + 8}px`
  }

  stylesRef.value = nextStyle
}

const updateTypeDropdownPosition = () => {
  setFloatingPanelPosition(typeButtonRef.value, 208, typeDropdownStyle)
}

const updatePointType = async (value: string) => {
  try {
    await updateCurrentPoint({ type: value })
    isTypeDropdownOpen.value = false
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to update point type', { error })
    showError(
      `Failed to save ${isWaypointMode.value ? 'waypoint' : 'POI'} type: ${
        error instanceof Error ? error.message : String(error)
      }`,
    )
    selectedPointType.value = currentPoint.value?.type ?? ''
  }
}

const toggleTypeDropdown = async () => {
  if (!canEditPoint.value) return
  if (!(await requirePermission('edit_waypoint'))) return
  isTypeDropdownOpen.value = !isTypeDropdownOpen.value
  if (isTypeDropdownOpen.value) {
    await nextTick()
    updateTypeDropdownPosition()
  }
}

const handlePointTypeChange = async (value: string) => {
  const previousType = currentPoint.value?.type ?? ''
  if (!canEditPoint.value) {
    selectedPointType.value = previousType
    return
  }
  if (!(await requirePermission('edit_waypoint'))) {
    selectedPointType.value = previousType
    return
  }
  selectedPointType.value = value
  if (value === previousType) return
  await updatePointType(value)
}

const copyTextToClipboard = async (value: string) => {
  try {
    if (!navigator.clipboard?.writeText) {
      throw new Error('Clipboard API not available')
    }
    await navigator.clipboard.writeText(value)
  } catch (error) {
    const textArea = document.createElement('textarea')
    textArea.value = value
    textArea.style.position = 'fixed'
    textArea.style.left = '-999999px'
    textArea.style.top = '-999999px'
    document.body.appendChild(textArea)
    textArea.focus()
    textArea.select()

    const successful = document.execCommand('copy')
    document.body.removeChild(textArea)

    if (!successful) {
      throw error instanceof Error ? error : new Error('Copy command failed')
    }
  }
}

const copyCoordinates = async () => {
  try {
    await copyTextToClipboard(coordinateText.value)
    showSuccess('Coordinates copied to clipboard')
  } catch (error) {
    logService.error(LOG_CATEGORIES.UI, 'Failed to copy coordinates', { error })
    showError(
      `Failed to copy coordinates: ${error instanceof Error ? error.message : String(error)}`,
    )
  }
}

const escapeXml = (value: string) =>
  value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&apos;')

const buildPointGpx = () => {
  const point = currentPoint.value
  if (!point) return ''

  const parts = [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<gpx version="1.1" creator="RouteCamp" xmlns="http://www.topografix.com/GPX/1/1">',
    `  <wpt lat="${point.lat}" lon="${point.lon}">`,
  ]

  if (typeof point.ele === 'number') {
    parts.push(`    <ele>${point.ele}</ele>`)
  }
  if (point.name?.trim()) {
    parts.push(`    <name>${escapeXml(point.name.trim())}</name>`)
  }
  if (point.description?.trim()) {
    parts.push(`    <desc>${escapeXml(point.description.trim())}</desc>`)
  }
  if (point.time) {
    parts.push(`    <time>${new Date(point.time).toISOString()}</time>`)
  }
  if (point.type?.trim()) {
    parts.push(`    <sym>${escapeXml(point.type.trim())}</sym>`)
  }

  parts.push('  </wpt>', '</gpx>')
  return parts.join('\n')
}

const downloadPoint = async () => {
  const gpx = buildPointGpx()
  if (!gpx) return

  const blob = new Blob([gpx], { type: 'application/gpx+xml' })
  const defaultName = isWaypointMode.value ? 'waypoint' : 'poi'
  const fileName = `${(currentPoint.value?.name?.trim() || defaultName).replace(/[\\/:*?"<>|]+/g, '_')}.gpx`

  try {
    await downloadFile(blob, fileName, { mimeType: 'application/gpx+xml' })
  } catch (error) {
    logService.error(LOG_CATEGORIES.API, 'Failed to download point GPX', { error })
    showError(
      `Failed to download ${isWaypointMode.value ? 'waypoint' : 'POI'}: ${
        error instanceof Error ? error.message : String(error)
      }`,
    )
  }
}

const handlePinPOI = async () => {
  if (!props.poi || isWaypointMode.value) return
  if (isPinnedPOI.value && isPinnedWorkspace.value) {
    emit('retain-unpinned-poi-detail', props.poi)
  }
  await togglePOIPinned(props.poi)
}

const openAddToCollectionPicker = () => {
  if (!authStore.isAuthenticated) {
    window.dispatchEvent(new CustomEvent('open-auth-modal', { detail: { view: 'login' } }))
    return
  }
  const point = currentPoint.value
  if (!point) return
  isTypeDropdownOpen.value = false
  openAddToCollection({
    points: [
      isWaypointMode.value
        ? waypointToAddToCollectionPoint(point as Waypoint)
        : poiToAddToCollectionPoint(point as WorkspacePOI),
    ],
    sourceWorkspaceId: isWaypointMode.value ? null : (point as WorkspacePOI).workspaceClientId,
  })
}

const handleCenter = () => {
  if (isWaypointMode.value) {
    if (!props.tourId || props.waypointIndex === null) return
    emit('zoom-waypoint', props.tourId, props.waypointIndex)
    return
  }

  if (!currentPointId.value) return
  emit('zoom-poi', currentPointId.value)
}

const handleVisibilityToggle = () => {
  if (isWaypointMode.value) {
    emit('toggle-waypoint-visibility')
    return
  }

  if (!currentPointId.value) return
  uiStore.toggleSinglePOIVisibility(currentPointId.value)
}

const requestDelete = async () => {
  if (!canDeletePoint.value) return

  if (isWaypointMode.value) {
    if (!props.tourId || props.waypointIndex === null) return
    emit('delete-waypoint', props.tourId, props.waypointIndex)
    return
  }

  if (!currentPointId.value) return
  emit('delete-poi', currentPointId.value)
}

useEventListener(document, 'pointerdown', (event: Event) => {
  const target = event.target as HTMLElement | null
  if (!target) return

  if (!target.closest('[data-poi-type-picker]') && !target.closest('[data-poi-type-button]')) {
    isTypeDropdownOpen.value = false
  }
})

useEventListener(window, 'resize', () => {
  if (isTypeDropdownOpen.value) updateTypeDropdownPosition()
})
</script>

<template>
  <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
    <div class="shrink-0">
      <div class="px-4 pt-3 pb-1">
        <div v-if="props.showHeaderControls" class="relative flex items-center justify-center">
          <div v-if="isWaypointMode" class="absolute left-0 top-1/2 -translate-y-1/2">
            <button
              type="button"
              class="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors hover:cursor-pointer"
              title="Back to tour"
              aria-label="Back to tour"
              @click="emit('back')"
            >
              <ArrowLeftIcon class="w-5 h-5" />
            </button>
          </div>
          <div class="flex items-center justify-center gap-0.5">
            <button
              type="button"
              class="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors hover:cursor-pointer"
              title="Center on map"
              aria-label="Center on map"
              @click="handleCenter"
            >
              <MaximizeIcon class="w-5 h-5" />
            </button>
            <button
              type="button"
              class="w-7 h-7 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors hover:cursor-pointer"
              :title="isPointVisible ? 'Hide on map' : 'Show on map'"
              :aria-label="isPointVisible ? 'Hide on map' : 'Show on map'"
              @click="handleVisibilityToggle"
            >
              <component :is="isPointVisible ? EyeIcon : EyeOffIcon" class="w-5 h-5" />
            </button>
          </div>
          <div class="absolute right-0 top-1/2 -translate-y-1/2">
            <button
              type="button"
              class="w-7 h-7 shrink-0 flex items-center justify-center rounded text-gray-600 hover:text-gray-900 hover:bg-gray-100 transition-colors hover:cursor-pointer"
              aria-label="Close panel"
              title="Close panel"
              @click="emit('close')"
            >
              <XIcon class="w-5 h-5" />
            </button>
          </div>
        </div>

        <div :class="props.showHeaderControls ? 'relative mt-2' : 'relative mt-1'">
          <div
            class="mx-auto grid w-full max-w-[calc(100%-2rem)] grid-cols-[1fr_auto_1fr] items-center"
          >
            <div aria-hidden="true"></div>

            <div class="group/title min-w-0">
              <div class="flex items-center justify-center gap-2">
                <span class="shrink-0 text-gray-500" data-testid="poi-title-icon">
                  <component :is="pointIcon" class="h-5 w-5" />
                </span>
                <div
                  class="min-w-0 rounded px-1.5 py-0.5 text-center transition-colors"
                  :class="
                    isNameEditing
                      ? 'ring-1 ring-emerald-500 bg-white'
                      : canEditPoint
                        ? `hover:ring-1 hover:ring-gray-300 ${isSmallScreen ? 'cursor-pointer' : 'cursor-text'}`
                        : ''
                  "
                  @click="canEditPoint && !isNameEditing && startNameEdit()"
                >
                  <div class="flex min-w-0 flex-1 items-baseline justify-center gap-1">
                    <input
                      v-if="isNameEditing"
                      ref="nameInputRef"
                      v-model="nameDraft"
                      type="text"
                      :class="pointNameInputClass"
                      @keydown.enter.prevent="saveNameEdit"
                      @keydown.esc.prevent="cancelNameEdit"
                      @blur="saveNameEdit"
                    />
                    <h2
                      v-else
                      class="min-w-0"
                      :class="pointNameDisplayClass"
                      :title="currentPoint?.name"
                    >
                      {{ currentPoint?.name || (isWaypointMode ? 'Waypoint' : 'POI') }}
                    </h2>
                    <span
                      v-if="!isNameEditing && pointCreatorByline"
                      class="shrink-0 text-xs font-normal text-gray-500"
                      data-testid="poi-detail-creator-byline"
                    >
                      {{ pointCreatorByline }}
                    </span>
                  </div>
                </div>
                <BaseIconButton
                  v-if="isSmallScreen && canEditPoint"
                  size="sm"
                  class="shrink-0"
                  title="Rename"
                  :aria-label="isWaypointMode ? 'Rename waypoint' : 'Rename POI'"
                  :data-testid="isWaypointMode ? 'waypoint-detail-rename' : 'poi-detail-rename'"
                  @click.stop="startNameEdit"
                >
                  <PencilIcon class="h-3.5 w-3.5" />
                </BaseIconButton>
              </div>
            </div>
            <div aria-hidden="true"></div>
          </div>
          <p
            v-if="isWaypointMode && waypointTourReferenceName"
            class="mt-1 text-center text-sm text-gray-600"
            data-testid="waypoint-tour-reference"
          >
            in {{ waypointTourReferenceName }}
          </p>

          <button
            type="button"
            class="mt-1 inline-flex w-full items-center justify-center gap-1.5 text-center text-xs text-gray-600 transition-colors hover:text-gray-900 hover:cursor-pointer"
            title="Copy coordinates"
            data-testid="poi-coordinates-button"
            @click="copyCoordinates"
          >
            <span>{{ coordinateText }}</span>
            <CopyIcon class="h-3.5 w-3.5 shrink-0 text-gray-400" aria-hidden="true" />
            <span v-if="elevationText" class="mx-1 text-gray-400" aria-hidden="true">&bull;</span>
            <span v-if="elevationText">{{ elevationText }}</span>
          </button>
          <div
            class="mt-1 flex items-center justify-center gap-2 text-sm text-gray-900 leading-snug"
            data-testid="poi-type-row"
          >
            <button
              ref="typeButtonRef"
              type="button"
              data-poi-type-button
              data-testid="poi-type-button"
              class="inline-flex items-center gap-2 rounded-md border border-gray-300 bg-white px-2.5 py-1 text-sm text-gray-900 shadow-sm transition-colors hover:border-gray-400 hover:bg-gray-50 hover:cursor-pointer focus:outline-none focus:ring-2 focus:ring-emerald-200 disabled:cursor-default disabled:bg-gray-50 disabled:text-gray-500"
              :disabled="!canEditPoint"
              :title="
                canEditPoint
                  ? `Change ${isWaypointMode ? 'waypoint' : 'POI'} type`
                  : `You do not have permission to change the ${isWaypointMode ? 'waypoint' : 'POI'} type`
              "
              @click.stop="toggleTypeDropdown"
            >
              <component :is="pointIcon" class="h-4 w-4 shrink-0 text-gray-500" />
              <span>{{ selectedPointTypeLabel }}</span>
              <ChevronDownIcon class="h-4 w-4 shrink-0 text-gray-400" />
            </button>
          </div>
        </div>
      </div>
    </div>

    <div class="min-h-0 flex-1 overflow-y-auto">
      <div class="px-3 pt-3 pb-2" data-testid="quick-actions">
        <div class="flex justify-center gap-2">
          <BaseActionButton
            v-if="!isWaypointMode && poi"
            :icon="isPinnedPOI ? PinOffIcon : PinIcon"
            :title="isPinnedPOI ? 'Unpin' : 'Pin'"
            :class="
              isPinnedPOI
                ? '!bg-amber-50 !text-amber-700 ring-1 ring-amber-200 hover:!bg-amber-100 hover:!text-amber-800'
                : ''
            "
            @click="handlePinPOI"
          >
            {{ isPinnedPOI ? 'Unpin' : 'Pin' }}
          </BaseActionButton>
          <BaseActionButton :icon="DownloadIcon" title="Download GPX" @click="downloadPoint">
            Download GPX
          </BaseActionButton>
          <button
            type="button"
            data-poi-copy-button
            class="flex h-20 w-20 flex-col items-center justify-center gap-1 rounded-lg bg-gray-100 text-gray-600 transition-colors hover:cursor-pointer hover:bg-gray-200 hover:text-gray-900"
            title="Add to collection"
            @click="openAddToCollectionPicker"
          >
            <SquarePlusIcon class="w-6 h-6 shrink-0" />
            <span class="text-center text-xs font-medium leading-tight">Add to collection</span>
          </button>
        </div>
      </div>

      <div class="mx-4 mt-3 mb-3 border-t border-gray-200"></div>

      <div class="px-4 pt-3 pb-4">
        <p class="text-xs font-medium text-gray-400">Description</p>
        <div
          class="mt-2 rounded px-1.5 py-0.5 -mx-1.5 transition-colors"
          :class="
            isDescriptionEditing
              ? 'ring-1 ring-emerald-500 bg-white'
              : canEditPoint
                ? 'hover:ring-1 hover:ring-gray-300 cursor-text'
                : ''
          "
          @click="canEditPoint && !isDescriptionEditing && startDescriptionEdit()"
        >
          <template v-if="!isDescriptionEditing">
            <p
              v-if="currentPoint?.description"
              class="text-sm text-gray-900 whitespace-pre-wrap"
              :class="descriptionExpanded ? '' : 'line-clamp-6'"
            >
              {{ currentPoint.description }}
            </p>
            <p v-else-if="canEditPoint" class="text-sm text-gray-400 italic">
              Add a description...
            </p>
            <button
              v-if="
                currentPoint?.description &&
                !descriptionExpanded &&
                currentPoint.description.length > 300
              "
              class="mt-1 text-xs text-emerald-600 hover:text-emerald-700 hover:cursor-pointer"
              @click.stop="descriptionExpanded = true"
            >
              Show more
            </button>
            <button
              v-if="
                currentPoint?.description &&
                descriptionExpanded &&
                currentPoint.description.length > 300
              "
              class="mt-1 text-xs text-emerald-600 hover:text-emerald-700 hover:cursor-pointer"
              @click.stop="descriptionExpanded = false"
            >
              Show less
            </button>
          </template>
          <template v-else>
            <textarea
              ref="descriptionTextareaRef"
              v-model="descriptionDraft"
              rows="3"
              class="w-full text-sm rounded px-0 py-0 resize-none focus:outline-none overflow-hidden bg-transparent"
              placeholder="Add a description..."
              @click.stop
              @input="autoSizeDescriptionTextarea"
              @keydown.enter.exact.prevent="saveDescriptionEdit"
              @keydown.shift.enter.stop
              @keydown.esc.prevent="cancelDescriptionEdit"
              @blur="saveDescriptionEdit"
            />
          </template>
        </div>
      </div>
    </div>

    <div
      class="shrink-0 border-t border-gray-200 bg-white px-4 py-1"
      data-testid="secondary-actions"
    >
      <div class="flex flex-col">
        <BaseActionButton
          v-if="canDeletePoint"
          layout="row"
          variant="danger"
          :icon="Trash2Icon"
          :title="isWaypointMode ? 'Delete waypoint' : 'Delete POI'"
          @click="requestDelete"
        >
          Delete
        </BaseActionButton>
      </div>
    </div>

    <Teleport to="body">
      <div
        v-if="isTypeDropdownOpen"
        data-poi-type-picker
        data-testid="poi-type-picker"
        :style="typeDropdownStyle"
        class="fixed z-9999 overflow-hidden rounded-md border border-gray-200 bg-white shadow-lg"
        @click.stop
      >
        <div class="max-h-64 overflow-y-auto py-1">
          <button
            v-for="option in poiTypeOptions"
            :key="option.value || 'none'"
            type="button"
            class="flex w-full items-center gap-2 px-3 py-2 text-left text-sm transition-colors hover:bg-gray-50 hover:cursor-pointer"
            :class="
              option.value === selectedPointType
                ? 'bg-emerald-50 text-emerald-700'
                : 'text-gray-800'
            "
            @click.stop="handlePointTypeChange(option.value)"
          >
            <component
              :is="getPOIIcon(option.value || undefined)"
              class="h-4 w-4 shrink-0 text-gray-500"
            />
            <span class="flex-1 truncate">{{ option.label }}</span>
            <CheckIcon
              v-if="option.value === selectedPointType"
              class="h-3.5 w-3.5 shrink-0 text-emerald-600"
            />
          </button>
        </div>
      </div>
    </Teleport>
  </div>
</template>
